import { ChangeEvent, JSX, useEffect, useRef, useState } from 'react';
import { Menu, useSidebarState, useStoreContext, usePermissions } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { Box, IconButton, InputAdornment, SxProps, TextField, Theme, Tooltip } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { CollapsibleMenuItem } from './CollapsibleMenuItem';
import { MenuSectionHeader } from './MenuSectionHeader';
import {
    Gated,
    MenuEntry,
    MenuGroup,
    MenuNode,
    MenuSection,
    entryLabel,
    entryMatchesPath,
    menuTree,
    sectionStoreKey,
} from './menuConfig';

/** Sections are title-cased by the theme rather than in every label. */
const menuStyles: SxProps<Theme> = {
    textTransform: 'capitalize',
    paddingTop: 0,
    overflowX: 'hidden',
};

/**
 * What the rail does to the plain items — the dashboard and the two top-level
 * links, which are react-admin's own `MenuItemLink` and not ours.
 *
 * At rail width that component still renders its label and simply lets the
 * sidebar clip it, which is why the old collapsed menu had glyphs at three
 * different x positions: each one was pushed over by whatever text sat beside
 * it. Squaring the row and dropping the label puts every icon on one axis, and
 * react-admin's own tooltip already says what each one is.
 */
const railMenuStyles: SxProps<Theme> = {
    '& .RaMenuItemLink-root': {
        width: 44,
        height: 40,
        minHeight: 40,
        margin: '2px auto',
        padding: 0,
        justifyContent: 'center',
        borderRadius: '10px',
        borderLeftWidth: 0,
    },
    '& .RaMenuItemLink-root .RaMenuItemLink-icon': { minWidth: 0, justifyContent: 'center' },
    '& .RaMenuItemLink-root .MuiTypography-root': { display: 'none' },
};

/** Long resource names wrap rather than overflow the sidebar. */
const itemStyles: SxProps<Theme> = { textWrap: 'auto' };

/** The filter row is UI chrome, so it opts out of the menu's capitalisation. */
const toolbarStyles: SxProps<Theme> = (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    padding: '10px 10px 12px',
    margin: '0 0 4px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    textTransform: 'none',
});

/** The same row at rail width: one button, centred, that gives the field back. */
const railToolbarStyles: SxProps<Theme> = (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: '10px 0 12px',
    margin: '0 0 4px',
    borderBottom: `1px solid ${theme.palette.divider}`,
});

/** A search field in a sidebar is not a form field; it should not look like one. */
const filterFieldStyles: SxProps<Theme> = (theme: Theme) => ({
    '& .MuiInputBase-root': {
        borderRadius: '8px',
        backgroundColor: theme.palette.action.hover,
        paddingInline: '8px',
        fontSize: '0.8125rem',
        '&::before, &::after': { display: 'none' },
    },
    '& .MuiInputBase-input': { paddingBlock: '7px' },
    '& .MuiSvgIcon-root': { fontSize: 18, color: theme.palette.text.secondary },
});

const emptyStyles: SxProps<Theme> = {
    padding: '12px 16px',
    color: 'text.secondary',
    fontSize: '0.8125rem',
    textTransform: 'none',
};

/** Entries are stacked in a column so wrapping each group changes nothing visually. */
const groupStyles: SxProps<Theme> = { display: 'flex', flexDirection: 'column' };

/** Absent role, nothing gated is visible — matches permissionManager, which rejects unknown roles. */
const NO_PERMISSIONS: string = '';

const isVisible: (item: Gated, permissions: string) => boolean =
    (item: Gated, permissions: string): boolean =>
        item.visible === undefined || item.visible(permissions);

const matchesQuery: (text: string, query: string) => boolean =
    (text: string, query: string): boolean =>
        query === '' || text.toLowerCase().includes(query);

/** Stable per-group id, so the heading can label its group without a hook in a loop. */
const groupId: (sectionTitle: string, heading: string) => string =
    (sectionTitle: string, heading: string): string =>
        `menu-group-${sectionTitle}-${heading}`.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const renderEntry: (entry: MenuEntry) => JSX.Element =
    (entry: MenuEntry): JSX.Element =>
        entry.kind === 'resource'
            ? <Menu.ResourceItem key={ entry.name } name={ entry.name } />
            : <Menu.Item
                key={ entry.to }
                to={ entry.to }
                primaryText={ entry.label }
                leftIcon={ entry.icon }
                sx={ itemStyles }
            />;

/**
 * Drops what the role cannot see and what the filter excludes, then drops any group
 * left empty. Matching a section title or a group heading keeps everything beneath it,
 * so typing "stripe" or "system" behaves the way you would expect.
 */
const visibleGroups: (section: MenuSection, permissions: string, query: string) => MenuGroup[] =
    (section: MenuSection, permissions: string, query: string): MenuGroup[] => {
        const sectionMatches: boolean = matchesQuery(section.title, query);

        return section.groups
            .filter((group: MenuGroup): boolean => isVisible(group, permissions))
            .map((group: MenuGroup): MenuGroup => {
                const keepAll: boolean = sectionMatches || matchesQuery(group.heading, query);
                return {
                    ...group,
                    entries: group.entries.filter((entry: MenuEntry): boolean =>
                        isVisible(entry, permissions) && (keepAll || matchesQuery(entryLabel(entry), query))),
                };
            })
            .filter((group: MenuGroup): boolean => group.entries.length > 0);
    };

const renderSection: (section: MenuSection, permissions: string, query: string, pathname: string) => JSX.Element | null =
    (section: MenuSection, permissions: string, query: string, pathname: string): JSX.Element | null => {
        const groups: MenuGroup[] = visibleGroups(section, permissions, query);
        if (groups.length === 0) {
            return null;
        }

        const containsActiveRoute: boolean = groups.some((group: MenuGroup): boolean =>
            group.entries.some((entry: MenuEntry): boolean => entryMatchesPath(entry, pathname)));

        return (
            <CollapsibleMenuItem
                key={ section.title }
                title={ section.title }
                icon={ section.icon }
                forceOpen={ query !== '' }
                containsActiveRoute={ containsActiveRoute }
            >
                { groups.map((group: MenuGroup, index: number): JSX.Element => {
                    const headingId: string = groupId(section.title, group.heading);
                    return (
                        <Box key={ group.heading } sx={ groupStyles } role="group" aria-labelledby={ headingId }>
                            <MenuSectionHeader id={ headingId } label={ group.heading } first={ index === 0 } />
                            { group.entries.map(renderEntry) }
                        </Box>
                    );
                }) }
            </CollapsibleMenuItem>
        );
    };

const renderNode: (node: MenuNode, permissions: string, query: string, pathname: string) => JSX.Element | null =
    (node: MenuNode, permissions: string, query: string, pathname: string): JSX.Element | null => {
        if (!isVisible(node, permissions)) {
            return null;
        }
        if (node.kind === 'section') {
            return renderSection(node, permissions, query, pathname);
        }
        return matchesQuery(entryLabel(node), query) ? renderEntry(node) : null;
    };

const AdminMenu: () => JSX.Element = (): JSX.Element => {
    // The generic is what types this: `permissions` is string | undefined, not any.
    const { permissions } = usePermissions();
    const role: string = permissions ?? NO_PERMISSIONS;
    const { pathname } = useLocation();
    const store = useStoreContext();
    const [filter, setFilter] = useState<string>('');
    const [sidebarOpen, setSidebarOpen] = useSidebarState();
    const filterInput = useRef<HTMLInputElement | null>(null);
    // Set when the field was asked for from the rail, so the focus lands after the
    // sidebar has actually reopened and the input exists to receive it.
    const focusOnOpen = useRef<boolean>(false);

    useEffect((): void => {
        if (sidebarOpen && focusOnOpen.current) {
            focusOnOpen.current = false;
            filterInput.current?.focus();
        }
    }, [sidebarOpen]);

    // A filter typed and then left behind would silently hide most of the menu the
    // next time the sidebar is opened, because the field it was typed into is gone.
    useEffect((): void => {
        if (!sidebarOpen) {
            setFilter('');
        }
    }, [sidebarOpen]);

    const query: string = filter.trim().toLowerCase();
    const nodes: JSX.Element[] = menuTree
        .map((node: MenuNode): JSX.Element | null => renderNode(node, role, query, pathname))
        .filter((node: JSX.Element | null): node is JSX.Element => node !== null);

    const collapseAll: () => void = (): void => {
        menuTree.forEach((node: MenuNode): void => {
            if (node.kind === 'section') {
                store.setItem(sectionStoreKey(node.title), false);
            }
        });
    };

    const openFilter: () => void = (): void => {
        focusOnOpen.current = true;
        setSidebarOpen(true);
    };

    return (
        <Menu sx={ sidebarOpen ? menuStyles : [menuStyles, railMenuStyles] }>
            { sidebarOpen
                ? <Box component="li" sx={ toolbarStyles }>
                    <TextField
                        value={ filter }
                        onChange={ (event: ChangeEvent<HTMLInputElement>): void => setFilter(event.target.value) }
                        placeholder="Filter menu"
                        size="small"
                        variant="standard"
                        fullWidth
                        inputRef={ filterInput }
                        sx={ filterFieldStyles }
                        slotProps={ {
                            input: {
                                disableUnderline: true,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: filter !== '' ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" aria-label="Clear filter" onClick={ (): void => setFilter('') }>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            },
                        } }
                    />
                    <Tooltip title="Collapse all sections">
                        <IconButton size="small" aria-label="Collapse all sections" onClick={ collapseAll }>
                            <UnfoldLessIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                : <Box component="li" sx={ railToolbarStyles }>
                    <Tooltip title="Filter menu" placement="right">
                        <IconButton size="small" aria-label="Filter menu" onClick={ openFilter }>
                            <SearchIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            }
            <Menu.DashboardItem />
            { nodes }
            { query !== '' && nodes.length === 0 &&
                <Box component="li" sx={ emptyStyles }>No menu items match “{ filter.trim() }”</Box>
            }
        </Menu>
    );
};

export default AdminMenu;
